/**
 * Days and timestamps, between the callables and the page.
 *
 * Callables return ISO timestamps; date inputs hold "YYYY-MM-DD"; the page
 * shows "17 Sep 2026" the way the policy list does. These are the three
 * conversions, in one place, because the first module got one of them wrong
 * on its first real record.
 */

import { formatDate } from '../features/policies/policyTable';

/**
 * A calendar date for a callable that wants an ISO date-time. Noon UTC, so a
 * reader who forgets to say UTC still sees the day that was typed, anywhere
 * from UTC-12 to UTC+11. The server keeps the same rule; the one place it is
 * written down is functions/src/utils/businessDate.ts.
 */
export function toIsoDateTime(date: string): string | undefined {
  return /^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T12:00:00.000Z` : undefined;
}

/**
 * THE CALENDAR DATE A STORED VALUE NAMES, READ IN UTC.
 *
 * Every screen that shows an effective date, a review date or an imported
 * legacy date goes through this. Five copies of it used to live in five hooks;
 * they agreed, but nothing made them agree, and the one renderer that did not
 * use any of them told approvers a policy effective on New Year's Day would
 * publish on 31 December.
 *
 * Reading in UTC is what makes it right, not the hour the value was stored at:
 * a value an older release wrote at UTC midnight names the same day here as
 * one written at noon, which is why no record had to be rewritten.
 *
 * A date-only field is never read with `getFullYear`, `getDate`,
 * `getMonth` or a `toLocaleDateString` without `timeZone: 'UTC'`; those read
 * the browser's zone and move the day. `businessDate.guard.test.ts` reads the
 * source and fails on a new one.
 */
export function calendarDate(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') return value.slice(0, 10);
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  }
  if (typeof value === 'object' && 'toDate' in (value as object)) {
    const d = (value as { toDate: () => Date }).toDate();
    return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
  }
  return null;
}

/** A timestamp, as the value of a date input. */
export function toDateInput(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

/** A timestamp shown as a day. formatDate wants a bare YYYY-MM-DD. */
export function formatDay(iso: string | null): string {
  return formatDate(iso ? iso.slice(0, 10) : null);
}

/** Whole days from today to a timestamp; negative when it has passed. */
export function daysUntil(iso: string | null, now = new Date()): number | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  return Math.round((then - now.getTime()) / 86_400_000);
}

/**
 * A DATE-ONLY VALUE, WITHOUT LOSING A DAY TO A TIME ZONE.
 *
 * `new Date('2026-10-15')` is parsed as UTC midnight, and
 * `toLocaleDateString` then renders it in local time — so in America/New_York
 * a contract term starting on the 15th displays as October 14. Every viewer
 * west of UTC saw yesterday's date, and nothing on screen said why.
 *
 * Split the string instead. A date with no time in it has no time zone to be
 * converted between, and pretending otherwise is what causes this.
 *
 * Month first, because the customers are US hospitals.
 */
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function formatDayUS(value: string | null | undefined): string | null {
  if (!value) return null;
  const [y, m, d] = value.slice(0, 10).split('-');
  const month = MONTHS_LONG[Number(m) - 1];
  if (!y || !d || !month) return value;
  return `${month} ${Number(d)}, ${y}`;
}
